import React from 'react';
import { useNavigate } from 'react-router-dom';
import logoImage from '../assets/jdLogo.png';

const TermsAndConditions: React.FC = () => {
  const navigate = useNavigate();

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={logoImage} alt="Just Deliveries Logo" className="h-16 w-16" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
            Terms & Conditions
          </h1>
          <p className="text-sm text-gray-600">
            JustDeliveries — Growth Logistics Private Limited
          </p>
        </div>

        {/* Content */}
        <div className="bg-white shadow-xl rounded-lg p-8">
          <div className="prose prose-lg max-w-none">
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                <strong>Effective Date:</strong> [12.09.2025]
              </p>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions ("Terms") apply to all services offered under the brand name JustDeliveries, 
                owned and operated by Growth Logistics Private Limited ("the Company," "we," "our," or "us"). By accessing, 
                registering with, or using our Platform, you acknowledge that you have read, understood, and agree to 
                these Terms, which constitute a legally binding agreement between you and JustDeliveries under the 
                Indian Contract Act, 1872.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Definitions</h2>
              <ul className="space-y-2 text-gray-700">
                <li><strong>"Platform"</strong> means the JustDeliveries website, mobile application, dashboards, APIs, and related systems.</li>
                <li><strong>"Services"</strong> means technology-enabled mid-mile cold-chain logistics, transport, delivery, invoicing, tracking, and related solutions offered by JustDeliveries.</li>
                <li><strong>"Client/User"</strong> means any individual, SME, or enterprise using the Platform or Services.</li>
                <li><strong>"Partner/Vendor"</strong> means third-party transport providers, drivers, or logistics agents authorized by JustDeliveries.</li>
                <li><strong>"Shipment/Consignment"</strong> means goods transported or booked through the Platform.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility</h2>
              <ul className="space-y-2 text-gray-700">
                <li>Users must be at least 18 years old and competent to contract under the Indian Contract Act, 1872.</li>
                <li>Business entities may use the Platform only through authorized representatives.</li>
                <li>By registering, you represent and warrant that you have the authority to bind yourself or your organization to these Terms.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Scope of Services</h2>
              <p className="text-gray-700 mb-4">We provide mid-mile logistics, including refrigerated, insulated, and ambient transport of perishable and temperature-sensitive goods.</p>
              <p className="text-gray-700 mb-4">Services may include:</p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li>• Vehicle booking, allocation, and live tracking</li>
                <li>• Temperature compliance monitoring with IoT devices</li>
                <li>• Automated SLA alerts and breach notifications</li>
                <li>• Proof of Delivery (POD) and invoicing</li>
                <li>• Wallet-based or credit-based payment mechanisms</li>
              </ul>
              <p className="text-gray-700 mt-4">JustDeliveries may engage third-party partners for fulfilment.</p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">User Responsibilities</h2>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Accurate Information:</strong> Provide true and complete booking, invoicing, and compliance data.</li>
                <li><strong>Packaging & Labelling:</strong> Ensure shipments are packed and labelled in line with FSSAI, transport, and safety norms.</li>
                <li><strong>Restricted Items:</strong> You must not ship illegal, hazardous, explosive, or otherwise prohibited goods.</li>
                <li><strong>Compliance:</strong> Clients remain responsible for ensuring goods comply with applicable laws, including the Food Safety and Standards Act, Drugs & Cosmetics Act, and others as relevant.</li>
                <li><strong>Account Security:</strong> Users must protect login credentials; all activity under your account will be deemed authorized by you.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Bookings, Pricing, and Payments</h2>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Pricing Models:</strong> Services are priced as per agreed contracts, published rate cards, or dynamic pricing (base fare + km/hr + wait-time + surcharges such as tolls/parking).</li>
                <li><strong>Invoices:</strong> Auto-generated invoices will be shared via the App/email. Discrepancies must be raised within 7 days.</li>
                <li><strong>Payment Terms:</strong> SMEs: Prepaid/wallet model. Enterprises: As per agreed credit period.</li>
                <li><strong>Taxes:</strong> All payments are exclusive of applicable GST and other levies.</li>
                <li><strong>Late Payments:</strong> Interest may be charged per month on overdue balances.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                JustDeliveries shall have the right to recover any outstanding dues and/or defaulted payments by exercising a lien, 
                or through auction or sale of the Client's goods, in the event the Client fails to remedy such default after 
                issuance of three (3) written notices, each providing a period of fifteen (15) days for compliance, without 
                any response or resolution from the Client.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Communication</h2>
              <ul className="space-y-2 text-gray-700">
                <li>Communication language shall be "English".</li>
                <li>All communications, including but not limited to notices, demands, and other formal correspondences, shall be deemed valid and effective only if made in writing and delivered either (i) by formal email to the registered email address of the Client, or (ii) by written notice sent to the Client's last known physical address as provided to JustDeliveries.</li>
                <li>For the purposes of this Agreement, both modes of communication shall carry equal legal validity, and any such communication shall be deemed to have been duly served upon successful dispatch to the aforesaid addresses.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Levels, Risk, and Liability</h2>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Best Efforts:</strong> We strive to maintain SLA compliance (timeliness, temperature, vehicle availability) but do not guarantee timelines unless explicitly contracted.</li>
                <li><strong>Risk of Goods:</strong> Shipments travel at client's risk unless insured.</li>
                <li><strong>Liability Cap:</strong> Our maximum liability per consignment shall not exceed freight charges paid.</li>
                <li><strong>Exclusions:</strong> No liability for indirect losses, lost profits, market loss, or spoilage due to incorrect packaging or force majeure events.</li>
                <li><strong>Insurance:</strong> Optional insurance coverage is available at client's cost, subject to insurer terms.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cancellation & Rescheduling</h2>
              <ul className="space-y-2 text-gray-700">
                <li>Orders may be cancelled/rescheduled before dispatch at no extra charge.</li>
                <li>Post-dispatch cancellations attract a cancellation fee equivalent to the trip minimum.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
              <ul className="space-y-2 text-gray-700">
                <li>All IP in the Platform (logos, software, algorithms, UI, dashboards, reports) belongs to JustDeliveries.</li>
                <li>Users are granted a limited, revocable, non-exclusive license to use the Platform solely for availing Services.</li>
                <li>Reverse engineering, scraping, or copying of the Platform is prohibited.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnity</h2>
              <p className="text-gray-700 mb-4">You agree to indemnify and hold harmless JustDeliveries, its officers, employees, and partners against claims, damages, or penalties arising from:</p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li>• Misuse of the Platform</li>
                <li>• Breach of these Terms</li>
                <li>• Shipping of prohibited goods</li>
                <li>• Non-compliance with applicable laws</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law and Dispute Resolution</h2>
              <ul className="space-y-2 text-gray-700">
                <li>These Terms are governed by the laws of India, including the Information Technology Act, 2000, Indian Contract Act, 1872.</li>
                <li><strong>Jurisdiction:</strong> Courts in Mumbai, Maharashtra.</li>
                <li>Disputes may first be attempted to be resolved amicably. If unresolved within 30 days, they shall be referred to arbitration under the Arbitration & Conciliation Act, 1996 with a sole arbitrator appointed mutually.</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Modifications</h2>
              <p className="text-gray-700">
                We may amend these Terms periodically. Updates will be posted on the Platform and effective immediately upon publication. 
                Continued use of the Platform implies acceptance.
              </p>
            </div>

            {/* Privacy Policy Section */}
            <div className="mb-8 border-t pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy Policy - JustDeliveries</h2>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Effective Date:</strong> [12.09.2025]
              </p>
              <p className="text-gray-700 mb-4">
                At JustDeliveries, we are committed to protecting your privacy and handling your data responsibly. 
                This Privacy Policy outlines how we collect, process, store, and safeguard personal information in 
                compliance with the Digital Personal Data Protection Act, 2023, the IT Act, 2000, and applicable Indian law.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Information We Collect</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li><strong>Personal Information:</strong> Name, email, phone number, billing/KYC details, GSTIN.</li>
                <li><strong>Transactional Information:</strong> Orders, invoices, payment history, wallet balance.</li>
                <li><strong>Operational Data:</strong> GPS location, temperature logs, route compliance, PODs.</li>
                <li><strong>Technical Data:</strong> Device type, IP address, browser details, cookies.</li>
                <li><strong>Communications:</strong> Support interactions, email/chat correspondences.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Purpose of Processing</h3>
              <p className="text-gray-700 mb-2">We use your data for:</p>
              <ul className="space-y-2 text-gray-700 ml-4 mb-4">
                <li>• Processing bookings, dispatches, and billing.</li>
                <li>• Providing live vehicle tracking, SLA reports, and compliance notifications.</li>
                <li>• Fraud detection, risk assessment, and operational analytics.</li>
                <li>• Meeting statutory obligations such as GST filings, transport records, and FSSAI compliance.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Sharing & Disclosure</h3>
              <ul className="space-y-2 text-gray-700 ml-4 mb-4">
                <li>• With logistics partners and drivers for order fulfilment.</li>
                <li>• With financial institutions and payment gateways for transactions.</li>
                <li>• With insurers, auditors, or regulators where required.</li>
                <li>• With cloud hosting and IT service providers for Platform operations.</li>
              </ul>
              <p className="text-gray-700 mb-4">We never sell or monetize user data.</p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Retention</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Personal data retained as long as necessary for services or legal compliance.</li>
                <li>Transactional records retained for 8 years (as per Income Tax/Companies Act).</li>
                <li>Location/operational data retained for 6 months before anonymization.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Security Safeguards</h3>
              <ul className="space-y-2 text-gray-700 ml-4 mb-4">
                <li>• Encryption of sensitive data in transit and storage.</li>
                <li>• Role-based access controls and audit trails.</li>
                <li>• Regular VAPT (Vulnerability Assessment & Penetration Testing).</li>
                <li>• Breach notification obligations as per DPDP Act.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">User Rights under DPDP Act, 2023</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li><strong>Right to Access:</strong> Obtain confirmation and copies of data.</li>
                <li><strong>Right to Correction/Erasure:</strong> Request rectification or deletion of data.</li>
                <li><strong>Right to Nominate:</strong> Appoint a nominee to exercise rights.</li>
                <li><strong>Right to Withdraw Consent:</strong> You may withdraw data usage consent anytime (subject to service continuity implications).</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cookies & Analytics</h3>
              <p className="text-gray-700 mb-4">
                The Platform uses cookies for session management, fraud prevention, and analytics. 
                Users can disable cookies in browser settings, but some features may not function.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Grievance Officer</h3>
              <p className="text-gray-700 mb-2">As mandated under IT Rules & DPDP Act:</p>
              <ul className="space-y-1 text-gray-700 mb-4">
                <li><strong>Name:</strong> Teesha Bokadia</li>
                <li><strong>Designation:</strong> Marketing Manager</li>
                <li><strong>Email:</strong> Complaint@jdindia.co.in</li>
                <li><strong>Address:</strong> 3rd Floor, Jolly Bhavan 2, 301B, New Marine Lines, Churchgate, Mumbai, Maharashtra 400020</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Updates</h3>
              <p className="text-gray-700">
                We may revise this Privacy Policy periodically. Updates will be posted on the Platform. 
                Continued usage implies consent.
              </p>
            </div>

            {/* Shipping & Delivery Policy Section */}
            <div className="mb-8 border-t pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Shipping & Delivery Policy - JustDeliveries</h2>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Effective Date:</strong> [12.09.2025]
              </p>
              <p className="text-gray-700 mb-4">
                This Shipping & Delivery Policy explains how JustDeliveries manages client bookings, 
                transport execution, and service obligations.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Scope of Services</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Mid-mile transportation of F&B, groceries, pharmaceuticals, and perishable goods requiring temperature compliance.</li>
                <li>Services via refrigerated vans, insulated carriers, and ambient vehicles.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Order Booking</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Orders may be placed via the App or pre-scheduled under contracts.</li>
                <li>Bookings are subject to vehicle and driver availability.</li>
                <li>Confirmation will be sent through the Platform and/or email.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Delivery Timelines</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Standard deliveries follow agreed service windows.</li>
                <li>Delays may occur due to traffic, regulatory checks, strikes, or natural calamities.</li>
                <li>SLA breach alerts are auto-generated and notified to clients.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Temperature & Quality Controls</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>All vehicles equipped with IoT sensors for live monitoring.</li>
                <li>Real-time breach alerts (SMS/app notification/call to driver).</li>
                <li>Clients can access logs via dashboards.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Charges & Billing</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Shipping charges calculated per rate card (base + km/hr + add-ons).</li>
                <li>Variable charges (parking, tolls, extra km/hr) auto-captured and added to invoices.</li>
                <li>SMEs: Prepaid wallet recharge.</li>
                <li>Enterprises: Monthly invoicing with credit terms.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Proof of Delivery (POD)</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Digital POD via OTP, signature, or image.</li>
                <li>Any shortage/damage must be reported within 24 hours of delivery.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cancellations & Rescheduling</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Cancellations before dispatch: No charge.</li>
                <li>Cancellations after dispatch: Charged at minimum trip value.</li>
                <li>Rescheduling allowed subject to availability.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Risk & Liability</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>Shipments are carried at client's risk unless insured.</li>
                <li>JustDeliveries' liability limited to freight charges paid.</li>
                <li>No liability for spoilage caused by poor packaging, mislabeling, or client delays.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Force Majeure</h3>
              <p className="text-gray-700 mb-4">
                We are not liable for failures due to events beyond our control including natural disasters, 
                strikes, riots, cyberattacks, or government restrictions.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Customer Support</h3>
              <p className="text-gray-700 mb-2">For delivery queries:</p>
              <ul className="space-y-1 text-gray-700">
                <li><strong>Email:</strong> Sales@jdindia.co.in</li>
                <li><strong>Support Hours:</strong> 10 am to 7 pm (Monday to Saturday)</li>
              </ul>
            </div>
          </div>

          {/* Back to Login Button */}
          <div className="mt-8 text-center">
            <button
              onClick={handleBackToLogin}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-jd-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jd-blue transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
